import { BarChart3, CheckCircle, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'

import { gradeRecordingStream } from '@/api/api'
import { GradeRecordingResponse, SegmentResponse, TranscribeResponse, WordResponse } from '@/api/type'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { useSmoothProgress } from '@/hooks/useSmoothProgress'
import { confirmDialog } from '@/lib/confirm-dialog'
import { SILENCE_TEXT } from '@/lib/constants'
import { deleteRecording, saveGradingResult, StoredRecording } from '@/lib/db'
import { formatDateTime, formatDuration } from '@/lib/time-format'

import { ControlButton } from '../playback-controls/control-button'
import { FluidAiLoader } from './fluid-ai-loader'
import { GradingChartDialog } from './grading-chart-dialog'
import { GradingResultDialog } from './grading-result-dialog'

type RecordingsSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  recordings: StoredRecording[]
  onRecordingsUpdate: () => void
  transcription: TranscribeResponse | null
  referenceAudioUrl?: string
  referenceWords?: WordResponse[]
  segments?: SegmentResponse[]
}

export const RecordingsSheet = ({
  open,
  onOpenChange,
  recordings,
  onRecordingsUpdate,
  transcription,
  referenceAudioUrl,
  referenceWords,
  segments,
}: RecordingsSheetProps) => {
  const [gradingId, setGradingId] = useState<string | null>(null)
  const [showGradingDialog, setShowGradingDialog] = useState(false)
  const [selectedGradingResult, setSelectedGradingResult] = useState<GradeRecordingResponse | null>(null)
  const [selectedRecording, setSelectedRecording] = useState<StoredRecording | null>(null)
  const [aiSummaryEnabled, setAiSummaryEnabled] = useState(true)
  const [showChartDialog, setShowChartDialog] = useState(false)
  const [targetProgress, setTargetProgress] = useState<number>(0)

  // 進捗をスムーズにアニメーション（カスタムフック使用）
  const gradingProgress = useSmoothProgress(targetProgress)

  // シートが開いたときに録音リストを取得
  useEffect(() => {
    if (open) {
      onRecordingsUpdate()
    }
  }, [open, onRecordingsUpdate])

  // 録音のBlobURLをメモ化（メモリリーク防止）
  const recordingUrls = useMemo(() => {
    const urls = new Map<string, string>()
    recordings.forEach((recording) => {
      urls.set(recording.id, URL.createObjectURL(recording.audioBlob))
    })
    return urls
  }, [recordings])

  // BlobURLのクリーンアップ
  useEffect(() => {
    return () => {
      // コンポーネントのアンマウント時またはrecordings変更時に古いURLを解放
      recordingUrls.forEach((url) => {
        URL.revokeObjectURL(url)
      })
    }
  }, [recordingUrls])

  // 録音削除ハンドラー
  const handleDeleteRecording = useCallback(
    async (id: string) => {
      if (!(await confirmDialog({ title: 'この録音を削除しますか？', destructive: true }))) return

      try {
        await deleteRecording(id)
        toast.success('録音を削除しました')
        onRecordingsUpdate()
      } catch (error) {
        console.error('Failed to delete recording:', error)
        toast.error('録音の削除に失敗しました')
      }
    },
    [onRecordingsUpdate],
  )

  // 採点ハンドラー
  const handleGradeRecording = useCallback(
    async (recording: StoredRecording) => {
      // すでに採点結果がある場合はダイアログで表示
      if (recording.gradingResult) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { gradedAt, ...result } = recording.gradingResult
        setSelectedGradingResult(result)
        setSelectedRecording(recording)
        setShowGradingDialog(true)
        return
      }

      if (!transcription) {
        toast.error('文字起こし情報がありません')
        return
      }

      setGradingId(recording.id)

      try {
        const referenceText = transcription.segments
          .filter((segment) => !(segment.text === SILENCE_TEXT || segment.skipped))
          .map((segment) => segment.text)
          .join(' ')
          .trim()

        if (!referenceText) {
          toast.error('参照テキストが見つかりません')
          setGradingId(null)
          return
        }

        // 進捗をリセット
        setTargetProgress(0)

        const response = await gradeRecordingStream(
          {
            audio: recording.audioBlob,
            referenceText,
            useAI: aiSummaryEnabled,
          },
          (progress) => {
            setTargetProgress(progress)
          },
        )

        // 採点結果を保存
        await saveGradingResult(recording.id, response)

        // 100%到達後、裏側でダイアログを準備
        setTargetProgress(100)

        // 裏側で結果ダイアログを開く（FluidAiLoaderの下に隠れている状態）
        setSelectedGradingResult(response)
        setSelectedRecording(recording)
        setShowGradingDialog(true)

        // リストを更新
        onRecordingsUpdate()
      } catch (error) {
        console.error('[Grading] Error occurred:', error)

        // エラーメッセージを抽出
        const errorMessage = error instanceof Error ? error.message : '不明なエラー'

        // より詳細なエラーメッセージを表示
        if (errorMessage.includes('I/O operation on closed file')) {
          toast.error(
            '採点に失敗しました\n\n' +
              '【原因】サーバー側でファイル処理エラーが発生しました。\n' +
              '音声ファイルの保存処理で問題が発生している可能性があります。\n\n' +
              '【対処法】\n' +
              '1. もう一度「採点」ボタンを押してリトライ\n' +
              '2. それでもダメなら、新しく録音し直してください\n' +
              '3. 問題が続く場合は、サーバー側のログを確認してください',
            {
              duration: 8000,
            },
          )
        } else if (errorMessage.includes('HTTP error')) {
          toast.error(`採点に失敗しました\n\nサーバーエラー: ${errorMessage}\n\nネットワーク接続を確認してください。`, {
            duration: 5000,
          })
        } else {
          toast.error(`採点に失敗しました\n\nエラー: ${errorMessage}`, {
            duration: 5000,
          })
        }

        // エラー時は状態をリセット
        setGradingId(null)
        setTargetProgress(0)
      }
    },
    [transcription, onRecordingsUpdate, aiSummaryEnabled],
  )

  // 結果を表示するハンドラー
  const handleShowResult = useCallback(() => {
    // FluidAiLoaderを閉じる（裏側の結果ダイアログが表示される）
    setGradingId(null)

    // 進捗状態のクリーンアップ
    setTimeout(() => {
      setTargetProgress(0)
    }, 300)
  }, [])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[80vh] overflow-y-auto gap-0">
        <SheetHeader className="relative">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-15 top-4 h-8 w-8 [&_svg]:w-7! [&_svg]:h-7!"
            onClick={() => {
              const gradedCount = recordings.filter((r) => r.gradingResult).length
              if (gradedCount === 0) {
                toast.info('採点データがありません。\nまず録音を採点してください。')
              } else {
                setShowChartDialog(true)
              }
            }}
          >
            <BarChart3 className="h-4 w-4" />
          </Button>
          <SheetTitle>録音リスト</SheetTitle>
          <SheetDescription>
            {recordings.length > 0 ? `${recordings.length}件の録音` : '録音がありません'}
          </SheetDescription>
        </SheetHeader>

        {/* AIで要約トグル */}
        <div className="mx-4 mt-4 mb-2 flex items-center justify-between">
          <label htmlFor="ai-summary-switch" className="text-sm font-medium">
            AIで要約
          </label>
          <Switch id="ai-summary-switch" checked={aiSummaryEnabled} onCheckedChange={setAiSummaryEnabled} />
        </div>

        <div className="space-y-3 flex flex-col gap-2 m-4">
          {recordings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>録音がありません</p>
            </div>
          ) : (
            recordings.map((recording, index) => (
              <div key={recording.id}>
                <div className="hover:bg-accent transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium">{formatDateTime(recording.savedAt)}</span>
                      </div>
                      <div className="space-y-1 text-xs text-muted-foreground">
                        <p>録音時間: {formatDuration(recording.duration)}</p>
                        <p>ファイルサイズ: {(recording.fileSize / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    {gradingId === recording.id &&
                      createPortal(
                        <div className="fixed inset-0 z-100 bg-gray-900/98 backdrop-blur-md flex items-center justify-center pointer-events-auto touch-auto">
                          <div className="relative w-full h-full max-w-4xl mx-auto bg-gray-900 flex flex-col items-center justify-center pointer-events-auto">
                            <FluidAiLoader progress={gradingProgress} onComplete={handleShowResult} />
                          </div>
                        </div>,
                        document.body,
                      )}
                    {gradingId !== recording.id && (
                      <ControlButton
                        onClick={() => handleGradeRecording(recording)}
                        icon={<CheckCircle className={`h-4 w-4 ${recording.gradingResult ? 'text-green-500' : ''}`} />}
                        label="採点"
                      />
                    )}
                    <ControlButton
                      onClick={() => handleDeleteRecording(recording.id)}
                      icon={<Trash2 className="h-4 w-4" />}
                      label="削除"
                    />
                  </div>
                  <audio controls src={recordingUrls.get(recording.id)} className="w-full h-10 mt-2" />
                </div>
                {index < recordings.length - 1 && <Separator className="mt-3" />}
              </div>
            ))
          )}
        </div>
      </SheetContent>

      {/* 採点結果ダイアログ */}
      <GradingResultDialog
        open={showGradingDialog}
        onOpenChange={(open) => {
          console.log('[GradingResultDialog] onOpenChange called with:', open)
          console.trace('[GradingResultDialog] Stack trace')
          setShowGradingDialog(open)
        }}
        result={selectedGradingResult}
        userAudioBlob={selectedRecording?.audioBlob}
        referenceAudioUrl={referenceAudioUrl}
        referenceWords={referenceWords}
        segments={segments}
      />

      {/* グラフダイアログ */}
      <GradingChartDialog open={showChartDialog} onOpenChange={setShowChartDialog} recordings={recordings} />
    </Sheet>
  )
}
